import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Calculator, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Preexistencia() {
  const [form, setForm] = useState({
    ref_catastral: "", direccion: "", provincia: "Madrid",
    tipo_inmueble: "Piso", calidad: "Estándar",
    m2_vivienda: 0, m2_trastero: 0, m2_garaje: 0, m2_zonas_comunes: 0, m2_otras_zonas: 0,
    producto: "1610", fecha_contratacion: "",
    capital_continente: 0, capital_contenido: 0, m2_declarados_poliza: 0,
    uso: "Principal", ocupantes: 1,
    danno_eur: 0, franquicia_eur: 0, tipo_danno: "continente",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setN = (k, v) => set(k, v === "" ? 0 : Number(v));

  const calcular = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      const r = await api.post("/preexistencia/calcular", form);
      setResult(r.data);
      toast.success("Cálculo completado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al calcular");
    } finally {
      setLoading(false);
    }
  };

  const copyInforme = async () => {
    if (!result?.informe_texto) return;
    try {
      await navigator.clipboard.writeText(result.informe_texto);
      toast.success("Informe copiado");
    } catch { toast.error("No se pudo copiar"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-[#2C2C2A]">Preexistencia e infraseguro</h1>
        <p className="text-[#7A786F] mt-1 text-sm">
          Calcula el valor real de continente y contenido, el % de infraseguro y la indemnización con regla proporcional a partir de la referencia catastral y el parte de siniestro.
        </p>
      </div>

      <form onSubmit={calcular} className="bg-white border border-[#E5E2D8] rounded-[14px] p-5 space-y-5">
        {/* Riesgo */}
        <div>
          <h2 className="font-heading text-sm font-semibold text-[#2C2C2A] mb-3">Riesgo</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><Label>Referencia catastral</Label><Input value={form.ref_catastral} onChange={(e) => set("ref_catastral", e.target.value)} className="rounded-[12px] border-[#E5E2D8]" data-testid="px-ref" /></div>
            <div className="md:col-span-2"><Label>Dirección</Label><Input value={form.direccion} onChange={(e) => set("direccion", e.target.value)} className="rounded-[12px] border-[#E5E2D8]" data-testid="px-direccion" /></div>
            <div><Label>Provincia *</Label><Input value={form.provincia} onChange={(e) => set("provincia", e.target.value)} className="rounded-[12px] border-[#E5E2D8]" data-testid="px-provincia" /></div>
            <div>
              <Label>Tipo de inmueble *</Label>
              <Select value={form.tipo_inmueble} onValueChange={(v) => set("tipo_inmueble", v)}>
                <SelectTrigger className="rounded-[12px] border-[#E5E2D8]" data-testid="px-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Piso">Piso en edificio</SelectItem>
                  <SelectItem value="Adosada">Unifamiliar adosada o pareada</SelectItem>
                  <SelectItem value="Independiente">Unifamiliar independiente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Calidad constructiva</Label>
              <Select value={form.calidad} onValueChange={(v) => set("calidad", v)}>
                <SelectTrigger className="rounded-[12px] border-[#E5E2D8]" data-testid="px-calidad"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Sencilla">Sencilla</SelectItem>
                  <SelectItem value="Estándar">Estándar (por defecto)</SelectItem>
                  <SelectItem value="Lujo">Lujo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Superficies */}
        <div>
          <h2 className="font-heading text-sm font-semibold text-[#2C2C2A] mb-3">Superficies (m²)</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ["m2_vivienda", "Vivienda *"],
              ["m2_trastero", "Trastero"],
              ["m2_garaje", "Garaje"],
              ["m2_zonas_comunes", "Zonas comunes"],
              ["m2_otras_zonas", "Otras zonas"],
            ].map(([k, label]) => (
              <div key={k}>
                <Label>{label}</Label>
                <Input type="number" min="0" step="0.01" value={form[k]} onChange={(e) => setN(k, e.target.value)} className="rounded-[12px] border-[#E5E2D8]" data-testid={`px-${k}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Póliza */}
        <div>
          <h2 className="font-heading text-sm font-semibold text-[#2C2C2A] mb-3">Póliza</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><Label>Producto *</Label><Input value={form.producto} onChange={(e) => set("producto", e.target.value)} placeholder="1610" className="rounded-[12px] border-[#E5E2D8]" data-testid="px-producto" /></div>
            <div><Label>Fecha contratación (solo 1680)</Label><Input type="date" value={form.fecha_contratacion} onChange={(e) => set("fecha_contratacion", e.target.value)} className="rounded-[12px] border-[#E5E2D8]" data-testid="px-fecha" /></div>
            <div><Label>Capital continente (€)</Label><Input type="number" min="0" step="0.01" value={form.capital_continente} onChange={(e) => setN("capital_continente", e.target.value)} className="rounded-[12px] border-[#E5E2D8]" data-testid="px-cap-cont" /></div>
            <div><Label>Capital contenido (€)</Label><Input type="number" min="0" step="0.01" value={form.capital_contenido} onChange={(e) => setN("capital_contenido", e.target.value)} className="rounded-[12px] border-[#E5E2D8]" data-testid="px-cap-contenido" /></div>
            <div><Label>m² declarados en póliza (solo m²)</Label><Input type="number" min="0" step="0.01" value={form.m2_declarados_poliza} onChange={(e) => setN("m2_declarados_poliza", e.target.value)} className="rounded-[12px] border-[#E5E2D8]" data-testid="px-m2-decl" /></div>
            <div>
              <Label>Uso</Label>
              <Select value={form.uso} onValueChange={(v) => set("uso", v)}>
                <SelectTrigger className="rounded-[12px] border-[#E5E2D8]" data-testid="px-uso"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Principal">Principal (FC 1,0)</SelectItem>
                  <SelectItem value="Secundaria">Secundaria (FC 0,5)</SelectItem>
                  <SelectItem value="Cedida en alquiler">Alquiler (FC 0,6)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Nº ocupantes</Label><Input type="number" min="0" value={form.ocupantes} onChange={(e) => setN("ocupantes", e.target.value)} className="rounded-[12px] border-[#E5E2D8]" data-testid="px-ocupantes" /></div>
          </div>
        </div>

        {/* Daño */}
        <div>
          <h2 className="font-heading text-sm font-semibold text-[#2C2C2A] mb-3">Daño (opcional, para regla proporcional)</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>Daño reclamado (€)</Label><Input type="number" min="0" step="0.01" value={form.danno_eur} onChange={(e) => setN("danno_eur", e.target.value)} className="rounded-[12px] border-[#E5E2D8]" data-testid="px-danno" /></div>
            <div><Label>Franquicia (€)</Label><Input type="number" min="0" step="0.01" value={form.franquicia_eur} onChange={(e) => setN("franquicia_eur", e.target.value)} className="rounded-[12px] border-[#E5E2D8]" data-testid="px-franq" /></div>
            <div>
              <Label>Tipo de daño</Label>
              <Select value={form.tipo_danno} onValueChange={(v) => set("tipo_danno", v)}>
                <SelectTrigger className="rounded-[12px] border-[#E5E2D8]" data-testid="px-tipo-danno"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="continente">Continente</SelectItem>
                  <SelectItem value="contenido">Contenido</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={loading} className="btn-eks-primary rounded-[12px]" data-testid="px-calcular">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calculator className="h-4 w-4 mr-2" />}
            Calcular
          </Button>
        </div>
      </form>

      {result && (
        <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-5 space-y-3" data-testid="px-result">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold text-[#2C2C2A]">Informe</h2>
            <Button onClick={copyInforme} variant="outline" className="border-[#E5E2D8] rounded-[12px]" data-testid="px-copiar">
              <Copy className="h-4 w-4 mr-1.5" /> Copiar informe
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="bg-[#FBFAF6] border border-[#E5E2D8] rounded-[10px] p-3"><div className="text-[10px] uppercase tracking-wide text-[#7A786F]">Continente</div><div className="font-semibold text-[#2C2C2A]" data-testid="px-total-cont">{result.preexistencia_continente?.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div><div className="text-xs text-[#7A786F]">{result.infraseguro_continente_pct ? `Infraseguro ${result.infraseguro_continente_pct}%` : "Sin infraseguro"}</div></div>
            <div className="bg-[#FBFAF6] border border-[#E5E2D8] rounded-[10px] p-3"><div className="text-[10px] uppercase tracking-wide text-[#7A786F]">Contenido</div><div className="font-semibold text-[#2C2C2A]" data-testid="px-total-contenido">{result.preexistencia_contenido?.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div><div className="text-xs text-[#7A786F]">{result.infraseguro_contenido_pct ? `Infraseguro ${result.infraseguro_contenido_pct}%` : "Sin infraseguro"}</div></div>
            {result.indemnizacion_eur != null && (
              <div className="bg-[#E6F5EF] border border-transparent rounded-[10px] p-3"><div className="text-[10px] uppercase tracking-wide text-[#1D9E75]">Indemnización (regla proporcional)</div><div className="font-semibold text-[#1D9E75]" data-testid="px-indemn">{result.indemnizacion_eur?.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div></div>
            )}
          </div>
          <Textarea readOnly value={result.informe_texto} rows={20} className="font-mono text-xs bg-[#FBFAF6] border-[#E5E2D8] rounded-[12px]" data-testid="px-informe-texto" />
        </div>
      )}
    </div>
  );
}
